import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Send, Search, RefreshCw, Inbox, Mail, Check, X, Clock,
  AlertCircle, ArrowRight, Sparkles, User, Building2, Package, Calendar, Filter, Paperclip,
} from 'lucide-react';
import FileDropzone from '../components/shared/FileDropzone';

const PRIORITY_CONFIG = {
  low:    { label: 'Scazut',  class: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  medium: { label: 'Mediu',   class: 'bg-blue-100 text-blue-800 border-blue-200' },
  high:   { label: 'Ridicat', class: 'bg-amber-100 text-amber-800 border-amber-200' },
  urgent: { label: 'URGENT',  class: 'bg-red-100 text-red-800 border-red-200 animate-pulse' },
};

const STATUS_CONFIG = {
  new:         { label: 'Nou',        class: 'bg-blue-100 text-blue-800 border-blue-200',    icon: Mail },
  accepted:    { label: 'Acceptat',   class: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Check },
  in_progress: { label: 'In lucru',   class: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  completed:   { label: 'Finalizat',  class: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: Check },
  rejected:    { label: 'Respins',    class: 'bg-rose-100 text-rose-800 border-rose-200',   icon: X },
};

const CATEGORY_ICON_MAP = {
  cleaning: Sparkles,
  equipment_prep: Package,
  visual_check: AlertCircle,
  logistics: Building2,
  shift_handover: User,
  other: MessageSquare,
};

const emptyForm = {
  title: '',
  description: '',
  category: 'cleaning',
  custom_category: '',
  priority: 'medium',
  to_user_id: '',
  equipment_id: '',
  location_id: '',
  plant_id: '',
  due_date: '',
};

const CrossDeptTasksPage = ({ basePath = '/cross-dept-tasks' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plants, locations } = useData();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({ new: 0, in_progress: 0, completed: 0, my_received_unread: 0, my_sent_open: 0 });
  const [categories, setCategories] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const fetchTasks = useCallback(async () => {
    try {
      const params = { scope, limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search && search.length >= 2) params.search = search;
      const res = await api.get('/cross-dept-tasks', { params });
      setTasks(res.data);
    } catch (e) {
      console.error('Failed to load tasks', e);
    }
  }, [scope, statusFilter, priorityFilter, categoryFilter, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/cross-dept-tasks/stats');
      setStats(res.data);
    } catch {}
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, eqRes, usersRes] = await Promise.all([
        api.get('/cross-dept-tasks/categories'),
        api.get('/equipment'),
        api.get('/cross-dept-tasks/assignable-users').catch(() => ({ data: [] })),
      ]);
      setCategories(catRes.data);
      setEquipmentOptions(eqRes.data || []);
      setUsersOptions(usersRes.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTasks(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchTasks, fetchStats]);

  // Filter equipment based on user's plant_access (section chiefs see only their plants)
  const accessibleEquipment = React.useMemo(() => {
    const userPlants = user?.plant_access || [];
    const userLocations = user?.location_access || [];
    // Admin/manager with no restrictions see everything
    if (userPlants.length === 0 && userLocations.length === 0) return equipmentOptions;
    return equipmentOptions.filter(eq => {
      const plantOk = userPlants.length === 0 || userPlants.includes(eq.plant_id);
      const locOk = userLocations.length === 0 || userLocations.includes(eq.location_id);
      return plantOk && locOk;
    });
  }, [equipmentOptions, user]);

  // Auto-fill plant/location when equipment changes
  const handleEquipmentChange = (equipmentId) => {
    if (!equipmentId || equipmentId === 'none') {
      setFormData(fd => ({ ...fd, equipment_id: '', plant_id: '', location_id: '' }));
      return;
    }
    const eq = equipmentOptions.find(e => e.id === equipmentId);
    setFormData(fd => ({
      ...fd,
      equipment_id: equipmentId,
      plant_id: eq?.plant_id || '',
      location_id: eq?.location_id || '',
    }));
  };

  const filteredLocations = locations.filter(l =>
    !formData.plant_id || l.plant_id === formData.plant_id
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.title.trim().length < 3) {
      toast.error('Titlul trebuie sa aiba minim 3 caractere');
      return;
    }
    if (formData.description.trim().length < 3) {
      toast.error('Descrierea este obligatorie');
      return;
    }
    if (!formData.to_user_id) {
      toast.error('Selectati persoana destinatara (obligatoriu)');
      return;
    }
    if (formData.category === 'other' && !formData.custom_category.trim()) {
      toast.error('Completati categoria personalizata');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        custom_category: formData.category === 'other' ? formData.custom_category.trim() : null,
        priority: formData.priority,
        to_user_id: formData.to_user_id,
        equipment_id: formData.equipment_id || null,
        location_id: formData.location_id || null,
        plant_id: formData.plant_id || null,
        due_date: formData.due_date || null,
      };
      const res = await api.post('/cross-dept-tasks', payload);
      const createdId = res.data?.id;

      // Upload pending attachments sequentially (keeps order + clear error messages)
      if (createdId && pendingFiles.length > 0) {
        setUploadProgress({ current: 0, total: pendingFiles.length });
        for (let i = 0; i < pendingFiles.length; i++) {
          const f = pendingFiles[i];
          const fd = new FormData();
          fd.append('file', f);
          try {
            await api.post(`/cross-dept-tasks/${createdId}/attachments`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUploadProgress({ current: i + 1, total: pendingFiles.length });
          } catch (upErr) {
            toast.error(`Fisierul "${f.name}" nu a putut fi incarcat: ${upErr.response?.data?.detail || 'eroare necunoscuta'}`);
          }
        }
      }

      toast.success(
        pendingFiles.length > 0
          ? `Task creat cu ${pendingFiles.length} atasament(e)`
          : 'Task interdepartamental creat cu succes'
      );
      setShowCreateDialog(false);
      setFormData(emptyForm);
      setPendingFiles([]);
      setUploadProgress({ current: 0, total: 0 });
      fetchTasks();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la creare task');
    } finally {
      setSubmitting(false);
    }
  };

  const openTask = (taskId) => navigate(`${basePath}/${taskId}`);

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const unreadForMe = (t) => !(t.read_by || []).includes(user?.id);

  const canUseModule = ['admin', 'manager', 'section_chief', 'technician'].includes(user?.role);
  if (!canUseModule) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-6 text-center text-slate-600">
          Acest modul este disponibil doar pentru tehnicieni, sefi sectie, manageri si admini.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 pb-24 md:pb-0" data-testid="cross-dept-tasks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-slate-900 flex items-center gap-2 md:gap-3">
            <MessageSquare className="w-6 h-6 md:w-9 md:h-9 text-indigo-600" />
            <span className="md:hidden">Task-uri Interdep.</span>
            <span className="hidden md:inline">Task-uri Interdepartamentale</span>
          </h1>
          <p className="hidden md:block text-slate-600 mt-1">
            Comunicare rapida intre mentenanta, sefi sectie si management — cereri de curatenie, pregatire utilaje, verificari, logistica.
          </p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button variant="outline" onClick={() => { fetchTasks(); fetchStats(); }} data-testid="refresh-tasks-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reincarca
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            Task Nou
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3">
        <StatCard icon={Mail} label="Necitite" value={stats.my_received_unread} color="bg-rose-100 text-rose-700" />
        <StatCard icon={Send} label="Trimise" value={stats.my_sent_open} color="bg-indigo-100 text-indigo-700" />
        <StatCard icon={Inbox} label="Noi" value={stats.new} color="bg-blue-100 text-blue-700" />
        <StatCard icon={Clock} label="In lucru" value={stats.in_progress} color="bg-amber-100 text-amber-700" />
        <StatCard icon={Check} label="Finalizate" value={stats.completed} color="bg-emerald-100 text-emerald-700" />
      </div>

      {/* Tabs scope */}
      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className="bg-slate-100 w-full md:w-auto grid grid-cols-3 md:inline-flex">
          <TabsTrigger value="all" data-testid="scope-all">Toate</TabsTrigger>
          <TabsTrigger value="received" data-testid="scope-received">Primite</TabsTrigger>
          <TabsTrigger value="sent" data-testid="scope-sent">Trimise</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 md:p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cauta titlu, echipament..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 md:h-10"
                data-testid="task-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 md:h-10"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate statusurile</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([v, c]) => (
                  <SelectItem key={v} value={v}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="h-11 md:h-10"><SelectValue placeholder="Prioritate" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate prioritatile</SelectItem>
                {Object.entries(PRIORITY_CONFIG).map(([v, c]) => (
                  <SelectItem key={v} value={v}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-11 md:h-10"><SelectValue placeholder="Categorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate categoriile</SelectItem>
                {categories.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : tasks.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-3" />
          <p>Nu exista task-uri pentru aceste filtre.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {tasks.map(t => {
            const CategoryIcon = CATEGORY_ICON_MAP[t.category] || MessageSquare;
            const status = STATUS_CONFIG[t.status] || STATUS_CONFIG.new;
            const priority = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.medium;
            const unread = unreadForMe(t);
            const StatusIcon = status.icon;
            return (
              <Card
                key={t.id}
                onClick={() => openTask(t.id)}
                className={`cursor-pointer hover:shadow-md active:scale-[0.99] transition-all ${unread ? 'border-indigo-400 ring-1 ring-indigo-200' : ''}`}
                data-testid={`task-card-${t.id}`}
              >
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <CategoryIcon className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 md:gap-2 mb-1">
                        {unread && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-600 text-white">NECITIT</span>}
                        <Badge className={`${status.class} border text-[10px] md:text-xs`}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
                        <Badge className={`${priority.class} border text-[10px] md:text-xs`}>{priority.label}</Badge>
                        {t.category === 'other' && t.custom_category && (
                          <Badge variant="outline" className="text-[10px] md:text-xs">{t.custom_category}</Badge>
                        )}
                        {t.promoted_to_ticket_id && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] md:text-xs">
                            <ArrowRight className="w-3 h-3 mr-1" /> Tichet
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm md:text-base line-clamp-2 md:truncate">{t.title}</h3>
                      <p className="text-xs md:text-sm text-slate-600 mt-1 line-clamp-2">{t.description}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] md:text-xs text-slate-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> <b className="text-slate-700">{t.from_user_name}</b></span>
                        <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> <b className="text-slate-700">{t.to_user_name || t.to_department}</b></span>
                        {t.equipment_name && (
                          <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {t.equipment_name}</span>
                        )}
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(t.created_at)}</span>
                        {t.comments?.length > 0 && (
                          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.comments.length}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Mobile FAB */}
      <button
        onClick={() => setShowCreateDialog(true)}
        className="md:hidden fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg active:scale-95 transition-transform flex items-center justify-center"
        data-testid="mobile-new-task-fab"
        aria-label="Task Nou"
      >
        <Plus className="w-6 h-6" />
      </button>
      <button
        onClick={() => { fetchTasks(); fetchStats(); }}
        className="md:hidden fixed bottom-20 left-4 z-40 w-12 h-12 rounded-full bg-white border border-slate-200 text-slate-700 shadow-lg active:scale-95 transition-transform flex items-center justify-center"
        data-testid="mobile-refresh-tasks-btn"
        aria-label="Reincarca"
      >
        <RefreshCw className="w-5 h-5" />
      </button>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] max-h-[90vh] overflow-y-auto p-4 md:p-6" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle>Task Interdepartamental Nou</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            <div className="space-y-2">
              <Label>Titlu <span className="text-red-500">*</span></Label>
              <Input className="h-11 md:h-10" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Ex: Curatenie utilaj X Urban" data-testid="task-title-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Categorie <span className="text-red-500">*</span></Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-11 md:h-10" data-testid="task-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioritate</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="h-11 md:h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([v, c]) => (<SelectItem key={v} value={v}>{c.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.category === 'other' && (
              <div className="space-y-2">
                <Label>Categorie personalizata <span className="text-red-500">*</span></Label>
                <Input
                  className="h-11 md:h-10"
                  value={formData.custom_category}
                  onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
                  placeholder="Ex: Eliberare zona, pregatire echipamente de protectie..."
                  data-testid="task-custom-category-input"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Descriere <span className="text-red-500">*</span></Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="Descrie exact ce trebuie facut, de ce, si pana cand..."
                data-testid="task-description-input"
              />
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-2">
                <Label>Persoana destinatara <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.to_user_id || ''}
                  onValueChange={(v) => setFormData({ ...formData, to_user_id: v })}
                >
                  <SelectTrigger className="h-11 md:h-10" data-testid="task-user-select">
                    <SelectValue placeholder="Selecteaza persoana..." />
                  </SelectTrigger>
                  <SelectContent>
                    {usersOptions.length === 0 ? (
                      <SelectItem value="__empty" disabled>Niciun utilizator disponibil</SelectItem>
                    ) : (
                      usersOptions.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name} — {u.role_label}{u.function ? ` (${u.function})` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Echipament <span className="text-slate-400 text-xs font-normal">(optional — auto-completeaza sediul si locatia)</span></Label>
              <Select
                value={formData.equipment_id || 'none'}
                onValueChange={(v) => handleEquipmentChange(v)}
              >
                <SelectTrigger className="h-11 md:h-10" data-testid="task-equipment-select">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— niciun echipament —</SelectItem>
                  {accessibleEquipment.slice(0, 300).map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.code}){eq.plant_name ? ` — ${eq.plant_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {accessibleEquipment.length === 0 && equipmentOptions.length > 0 && (
                <p className="text-[11px] text-amber-600">
                  Nu aveti acces la niciun echipament conform drepturilor setate (plant_access).
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Sediu <span className="text-slate-400 text-xs font-normal">(auto din echipament)</span></Label>
                <Select
                  value={formData.plant_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, plant_id: v === 'none' ? '' : v, location_id: '' })}
                  disabled={!!formData.equipment_id}
                >
                  <SelectTrigger className="h-11 md:h-10"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— niciunul —</SelectItem>
                    {plants.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Locatie <span className="text-slate-400 text-xs font-normal">(auto din echipament)</span></Label>
                <Select
                  value={formData.location_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, location_id: v === 'none' ? '' : v })}
                  disabled={!formData.plant_id || !!formData.equipment_id}
                >
                  <SelectTrigger className="h-11 md:h-10"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— niciuna —</SelectItem>
                    {filteredLocations.map(l => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Termen solicitat (optional)</Label>
              <Input className="h-11 md:h-10" type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-slate-500" />
                Atasamente <span className="text-slate-400 text-xs font-normal">(optional — poze, PDF, documente)</span>
              </Label>
              <FileDropzone
                files={pendingFiles}
                onFilesChange={setPendingFiles}
                disabled={submitting}
                testIdPrefix="new-task-files"
              />
              {submitting && uploadProgress.total > 0 && (
                <p className="text-xs text-indigo-600" data-testid="new-task-upload-progress">
                  Se incarca fisierele... ({uploadProgress.current}/{uploadProgress.total})
                </p>
              )}
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" className="w-full sm:w-auto h-11 md:h-10" onClick={() => { setShowCreateDialog(false); setFormData(emptyForm); setPendingFiles([]); }}>Anuleaza</Button>
              <Button type="submit" className="w-full sm:w-auto h-11 md:h-10 bg-indigo-600 hover:bg-indigo-700" disabled={submitting} data-testid="submit-task-btn">
                <Send className="w-4 h-4 mr-2" />
                {submitting ? 'Se trimite...' : 'Trimite task-ul'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="p-2.5 md:p-4">
      <div className="flex items-center gap-2 md:gap-3">
        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4 md:w-5 md:h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] md:text-xs text-slate-500 truncate">{label}</p>
          <p className="text-lg md:text-2xl font-bold">{value || 0}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default CrossDeptTasksPage;
